
import { Layout } from "@/components/Layout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { CategoryFilter } from "@/components/posts/CategoryFilter";
import { TabContent, TrendingTabContent, SupportTabContent } from "@/components/posts/TabContent";
import { HashtagCloud } from "@/components/hashtags/HashtagCloud";
import { FriendActivityNotifications } from "@/components/notifications/FriendActivityNotifications";
import { useToast } from "@/hooks/use-toast";
import { SubscriptionDialog } from "@/components/subscription/SubscriptionDialog";
import { Button } from "@/components/ui/button";
import { Crown } from "lucide-react";
import { useSubscription } from "@/hooks/useSubscription";

const Index = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { subscription, loading } = useSubscription();

  useEffect(() => {
    const checkAuthAndInvite = async () => {
      const subscriptionStatus = searchParams.get('subscription');
      
      if (subscriptionStatus === 'success') {
        toast({
          title: "✨ Welcome to Positivio Club! ✨",
          description: "Thank you for joining us. You now have 3 invitations to share with friends who are ready for calmer social media.",
          duration: 10000,
        });
        // Remove the parameter from URL
        navigate("/", { replace: true });
        return;
      }
    };

    checkAuthAndInvite();
  }, [searchParams, navigate, toast]);


  return (
    <Layout>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3">
          <div className="w-full max-w-full mx-auto px-4">
            <h1 className="text-4xl font-bold mb-6 bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent">
              Community Feed
            </h1>
            
            <Tabs defaultValue="recent" className="w-full mb-8">
              <div className="bg-gradient-to-br from-pink-100 to-purple-100 dark:from-pink-900/30 dark:to-purple-900/30 p-5 rounded-3xl shadow-lg mb-6 border border-pink-200">
                <TabsList className="w-full grid grid-cols-3 rounded-full bg-white/80 dark:bg-gray-800/80 p-1.5 backdrop-blur-md">
                  <TabsTrigger 
                    value="recent" 
                    className="data-[state=active]:bg-gradient-to-r from-pink-500 to-purple-500 data-[state=active]:text-white rounded-full py-2.5 transition-all duration-300"
                  >
                    Recent
                  </TabsTrigger>
                  <TabsTrigger 
                    value="trending" 
                    className="data-[state=active]:bg-gradient-to-r from-pink-500 to-purple-500 data-[state=active]:text-white rounded-full py-2.5 transition-all duration-300"
                  >
                    Trending
                  </TabsTrigger>
                  <TabsTrigger 
                    value="support" 
                    className="data-[state=active]:bg-gradient-to-r from-pink-500 to-purple-500 data-[state=active]:text-white rounded-full py-2.5 transition-all duration-300"
                  >
                    Support
                  </TabsTrigger>
                </TabsList>
              </div>

              <CategoryFilter 
                selectedCategory={selectedCategory} 
                onCategoryChange={setSelectedCategory} 
              />

              <TabsContent value="recent">
                <TabContent selectedCategory={selectedCategory} />
              </TabsContent>

              <TabsContent value="trending">
                <TrendingTabContent />
              </TabsContent>

              <TabsContent value="support">
                <SupportTabContent />
              </TabsContent>
            </Tabs>

            {/* Mobile hashtags - shown below content on small screens */}
            <div className="lg:hidden mb-6">
              <HashtagCloud />
            </div>
          </div>
        </div>
        
        {/* Desktop sidebar - hidden on mobile */}
        <div className="hidden lg:block space-y-6">
          <FriendActivityNotifications />
          <HashtagCloud />
        </div>
      </div>
    </Layout>
  );
};

export default Index;
